<template>
  <div>
    <b-form-row>
      <b-col cols="auto">
        <b-form-group
            :label="$t('Floor')"
            label-for="floor_id"
        >
          <b-input-group class="align-items-start">
            <floor-select
                id="floor_id"
                v-model="floor"
                :resource="`projects/${projectId}/floors`"
                class="w-auto"
                group-label="name"
                group-values="children"
                no-custom-slots
            />

            <b-input-group-append>
              <b-button @click="floor = null">
                <fa :title="$t('Clear')" icon="times"/>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>

      <b-col cols="auto">
        <b-form-group
            :label="$t('Search')"
            :label-for="`${uuid}_search`"
        >
          <search-input-group :id="`${uuid}_search`" v-model="params.search"/>
        </b-form-group>
      </b-col>

      <b-col cols="auto">
        <b-button
            v-b-toggle.walls-select-filters
            class="mt-4"
        >
          <b-badge pill v-if="filtersApplied" variant="info">
            <fa icon="exclamation" />
          </b-badge>

          {{ $t('Advanced filters') }}
        </b-button>
      </b-col>

      <b-col></b-col>

      <b-col cols="auto">
        <b-button
            variant="outline-secondary"
            class="mt-4"
            @click="resetFilters"
        >
          {{ $t('Reset filters') }}

          <fa icon="times"/>
        </b-button>
      </b-col>
    </b-form-row>

    <b-collapse id="walls-select-filters" class="mt-2" visible>
      <b-form-row>
        <b-col class="pb-1" cols="12" lg="6" xl="3">
          <b-card :sub-title="$t('Height')" body-class="pb-1">
            <b-row no-gutters>
              <b-col>
                <b-form-group :label="$t('from')">
                  <b-input-group size="sm">
                    <b-form-input
                        v-model="params.height_from"
                        step="0.001"
                        min="0.001"
                        type="number"
                    />

                    <template #append>
                      <b-input-group-text>
                        <span v-html="'m'"/>
                      </b-input-group-text>
                    </template>
                  </b-input-group>

                </b-form-group>
              </b-col>

              <b-col>
                <b-form-group :label="$t('to')">
                  <b-input-group size="sm">
                    <b-form-input
                        v-model="params.height_to"
                        step="0.001"
                        min="0.001"
                        type="number"
                    />
                    <template #append>
                      <b-input-group-text>
                        <span v-html="'m'"/>
                      </b-input-group-text>
                    </template>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>
          </b-card>
        </b-col>

        <b-col class="pb-1" cols="12" lg="6" xl="3">
          <b-card :sub-title="$t('Width')" body-class="pb-1">
            <b-row no-gutters>
              <b-col>
                <b-form-group :label="$t('from')">
                  <b-input-group size="sm">
                    <b-form-input
                        v-model="params.width_from"
                        step="0.001"
                        min="0.001"
                        type="number"
                    />
                    <template #append>
                      <b-input-group-text>
                        <span v-html="'m'"/>
                      </b-input-group-text>
                    </template>
                  </b-input-group>
                </b-form-group>
              </b-col>

              <b-col>
                <b-form-group :label="$t('to')">
                  <b-input-group size="sm">
                    <b-form-input
                        v-model="params.width_to"
                        step="0.001"
                        min="0.001"
                        type="number"
                    />
                    <template #append>
                      <b-input-group-text>
                        <span v-html="'m'"/>
                      </b-input-group-text>
                    </template>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>
          </b-card>
        </b-col>

        <b-col class="pb-1" cols="12" lg="6" xl="3">
          <b-card :sub-title="$t('Length')" body-class="pb-1">
            <b-row no-gutters>
              <b-col>
                <b-form-group :label="$t('from')">
                  <b-input-group size="sm">
                    <b-form-input
                        v-model="params.length_from"
                        step="0.001"
                        min="0.001"
                        type="number"
                    />
                    <template #append>
                      <b-input-group-text>
                        <span v-html="'m'"/>
                      </b-input-group-text>
                    </template>
                  </b-input-group>
                </b-form-group>
              </b-col>

              <b-col>
                <b-form-group :label="$t('to')">
                  <b-input-group size="sm">
                    <b-form-input
                        v-model="params.length_to"
                        step="0.001"
                        min="0.001"
                        type="number"
                    />
                    <template #append>
                      <b-input-group-text>
                        <span v-html="'m'"/>
                      </b-input-group-text>
                    </template>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>
          </b-card>
        </b-col>

        <b-col class="pb-1" cols="12" lg="6" xl="3">
          <b-card :sub-title="$t('Area')" body-class="pb-1">
            <b-row no-gutters>
              <b-col>
                <b-form-group :label="$t('from')">
                  <b-input-group size="sm">
                    <b-form-input
                        v-model="params.area_from"
                        step="0.001"
                        min="0.001"
                        type="number"
                    />
                    <template #append>
                      <b-input-group-text>
                        <span v-html="'m<sup>2</sup>'"/>
                      </b-input-group-text>
                    </template>
                  </b-input-group>
                </b-form-group>
              </b-col>

              <b-col>
                <b-form-group :label="$t('to')">
                  <b-input-group size="sm">
                    <b-form-input
                        v-model="params.area_to"
                        step="0.001"
                        min="0.001"
                        type="number"
                    />
                    <template #append>
                      <b-input-group-text>
                        <span v-html="'m<sup>2</sup>'"/>
                      </b-input-group-text>
                    </template>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-form-row>
    </b-collapse>

    <p>{{ $tc('Selected :count items', value.length, [value.length]) }}</p>

    <loading-bar v-if="pending" class="mx-n3"/>
    <tree-view
        v-else
        :disabled="disabledFunction"
        :error-prefix="errorPrefix"
        :errors="errors"
        :items="items"
        :value="value"
        :select-all-label="selectAllLabel"
        @input="$emit('input', $event)"
    />
  </div>
</template>

<script>
import TreeView from "@/components/TreeView";
import FloorSelect from '@/components/selects/ResourceSelect';
import {Errors} from 'vform';
import SearchInputGroup from "@/components/SearchInputGroup";
import fetchData from "@/mixins/fetchData";

export default {
  name: "WallsTreeSelect",
  components: {SearchInputGroup, TreeView, FloorSelect},
  mixins: [fetchData],
  props: {
    value: { // Walls which belong to project Foundation
      type: Array,
      default: () => []
    },
    projectId: {
      type: [Number, String],
      required: true
    },
    errors: {
      type: Errors,
      default: () => ({})
    },
    disabledFunction: {
      type: [Boolean, Function],
      default: null
    },
    selectAllLabel: {
      type: String,
      default() {
        return this.$t('Select all')
      }
    },
    errorPrefix: {
      type: String,
      default: ''
    },
    apiUrlOverride: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      floor: null,
      params: {
        floor_id: null,
        search: null,
        height_from: null,
        height_to: null,
        width_from: null,
        width_to: null,
        length_from: null,
        length_to: null,
        area_from: null,
        area_to: null,
      },
    }
  },
  computed: {
    apiUrl() {
      if (!this.floor) {
        return null;
      }

      if (this.apiUrlOverride) {
        return this.apiUrlOverride
      }

      return `/api/floors/${this.floor.id}/walls`;
    },
    filtersApplied() {
      return Object.values(this.params).some(x => x !== null);
    }
  },
  watch: {
    'floor': function (newValue) {
      this.params.floor_id = newValue ? newValue.id : null
    }
  },
  methods: {
    resetFilters() {
      for (let key in this.params ) {
        this.params[key] = null;
      }
    }
  }
}
</script>
